import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const ALERT_COLORS = {
  normal: '#22c55e',
  watch: '#f59e0b',
  alert: '#ef4444',
};

const ALERT_BG = {
  normal: '#f0fdf4',
  watch: '#fffbeb',
  alert: '#fef2f2',
};

const ALERT_BORDER = {
  normal: '#bbf7d0',
  watch: '#fde68a',
  alert: '#fecaca',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: FONT_STACK,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 13,
    color: '#6b7280',
    fontWeight: 400,
  },
  summaryBar: {
    display: 'flex',
    gap: 12,
    margin: '16px 0',
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 14px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: FONT_STACK,
  },
  pillDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  searchInput: {
    padding: '7px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: FONT_STACK,
    outline: 'none',
    minWidth: 220,
  },
  showAllBtn: {
    padding: '6px 14px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: FONT_STACK,
    background: '#f9fafb',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#374151',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    fontFamily: FONT_STACK,
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '9px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#111827',
    whiteSpace: 'nowrap',
  },
  trHover: {
    background: '#f9fafb',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  barContainer: {
    width: 100,
    height: 10,
    background: '#f3f4f6',
    borderRadius: 5,
    overflow: 'hidden',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
    color: '#9ca3af',
    fontSize: 14,
  },
  sortArrow: {
    marginLeft: 4,
    fontSize: 10,
    color: '#9ca3af',
  },
  sortArrowActive: {
    marginLeft: 4,
    fontSize: 10,
    color: '#111827',
  },
  scoreValue: {
    fontWeight: 600,
    marginRight: 8,
    display: 'inline-block',
    minWidth: 28,
  },
  componentValue: {
    fontVariantNumeric: 'tabular-nums',
  },
};

const COLUMNS = [
  { key: 'rank', label: 'Rank', sortable: false },
  { key: 'label', label: 'Address', sortable: true },
  { key: 'state', label: 'State', sortable: true },
  { key: 'compositeScore', label: 'Composite Score', sortable: true },
  { key: 'zScoreRank', label: 'Z-Score', sortable: true },
  { key: 'volatilityRank', label: 'Volatility', sortable: true },
  { key: 'deviationRank', label: 'Deviation', sortable: true },
  { key: 'missingDataScore', label: 'Missing Data', sortable: true },
  { key: 'alertLevel', label: 'Alert Level', sortable: true },
];

const ALERT_ORDER = { alert: 0, watch: 1, normal: 2 };
const DEFAULT_LIMIT = 20;

function scoreColor(score) {
  const colorScale = d3.scaleLinear()
    .domain([0, 5, 10])
    .range(['#22c55e', '#f59e0b', '#ef4444'])
    .clamp(true);
  return colorScale(score);
}

function getComponentValue(address, key) {
  if (key === 'zScoreRank') return address.components?.zScoreRank ?? 0;
  if (key === 'volatilityRank') return address.components?.volatilityRank ?? 0;
  if (key === 'deviationRank') return address.components?.deviationRank ?? 0;
  if (key === 'missingDataScore') return address.components?.missingDataScore ?? 0;
  return 0;
}

function getSortValue(address, key) {
  if (key === 'compositeScore') return address.compositeScore ?? 0;
  if (key === 'label') return (address.label || '').toLowerCase();
  if (key === 'state') return (address.state || '').toLowerCase();
  if (key === 'alertLevel') return ALERT_ORDER[address.alertLevel] ?? 3;
  if (['zScoreRank', 'volatilityRank', 'deviationRank', 'missingDataScore'].includes(key)) {
    return getComponentValue(address, key);
  }
  return 0;
}

function ScoreBar({ score }) {
  const pct = Math.max(0, Math.min(100, (score / 10) * 100));
  const color = scoreColor(score);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={styles.scoreValue}>{score.toFixed(1)}</span>
      <span style={styles.barContainer}>
        <span
          style={{
            display: 'block',
            width: `${pct}%`,
            height: '100%',
            borderRadius: 5,
            background: color,
            transition: 'width 0.2s ease',
          }}
        />
      </span>
    </span>
  );
}

function AlertBadge({ level }) {
  const bg = ALERT_BG[level] || ALERT_BG.normal;
  const color = ALERT_COLORS[level] || ALERT_COLORS.normal;
  const border = ALERT_BORDER[level] || ALERT_BORDER.normal;
  return (
    <span
      style={{
        ...styles.badge,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {level}
    </span>
  );
}

export default function AddressAnomalyScorecard({ data }) {
  const [sortKey, setSortKey] = useState('compositeScore');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const addresses = data?.addresses;

  const summary = useMemo(() => {
    if (!addresses || addresses.length === 0) return { alert: 0, watch: 0, normal: 0 };
    return addresses.reduce(
      (acc, a) => {
        const level = a.alertLevel || 'normal';
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      },
      { alert: 0, watch: 0, normal: 0 }
    );
  }, [addresses]);

  const sorted = useMemo(() => {
    if (!addresses || addresses.length === 0) return [];
    const filtered = search.trim()
      ? addresses.filter((a) =>
          (a.label || '').toLowerCase().includes(search.trim().toLowerCase())
        )
      : [...addresses];

    filtered.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (typeof va === 'string') {
        return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortAsc ? va - vb : vb - va;
    });

    return filtered;
  }, [addresses, sortKey, sortAsc, search]);

  const displayed = useMemo(() => {
    if (showAll) return sorted;
    return sorted.slice(0, DEFAULT_LIMIT);
  }, [sorted, showAll]);

  const handleSort = (key) => {
    if (!COLUMNS.find((c) => c.key === key)?.sortable) return;
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  if (!data || !addresses || addresses.length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Address Anomaly Scorecard</h3>
        <p style={styles.subtitle}>
          Composite anomaly ranking across all tracked addresses
        </p>
        <div style={styles.emptyState}>No address anomaly data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Address Anomaly Scorecard</h3>
      <p style={styles.subtitle}>
        Composite anomaly ranking across all tracked addresses
      </p>

      <div style={styles.summaryBar}>
        <span
          style={{
            ...styles.pill,
            background: ALERT_BG.alert,
            color: ALERT_COLORS.alert,
            border: `1px solid ${ALERT_BORDER.alert}`,
          }}
        >
          <span style={{ ...styles.pillDot, background: ALERT_COLORS.alert }} />
          {summary.alert} Alert
        </span>
        <span
          style={{
            ...styles.pill,
            background: ALERT_BG.watch,
            color: ALERT_COLORS.watch,
            border: `1px solid ${ALERT_BORDER.watch}`,
          }}
        >
          <span style={{ ...styles.pillDot, background: ALERT_COLORS.watch }} />
          {summary.watch} Watch
        </span>
        <span
          style={{
            ...styles.pill,
            background: ALERT_BG.normal,
            color: ALERT_COLORS.normal,
            border: `1px solid ${ALERT_BORDER.normal}`,
          }}
        >
          <span style={{ ...styles.pillDot, background: ALERT_COLORS.normal }} />
          {summary.normal} Normal
        </span>
      </div>

      <div style={styles.controls}>
        <input
          type="text"
          placeholder="Search addresses..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        {sorted.length > DEFAULT_LIMIT && (
          <button
            onClick={() => setShowAll(!showAll)}
            style={styles.showAllBtn}
          >
            {showAll
              ? `Show Top ${DEFAULT_LIMIT}`
              : `Show All (${sorted.length})`}
          </button>
        )}
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={styles.th}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable && sortKey === col.key && (
                    <span style={styles.sortArrowActive}>
                      {sortAsc ? ' \u25B2' : ' \u25BC'}
                    </span>
                  )}
                  {col.sortable && sortKey !== col.key && (
                    <span style={styles.sortArrow}> \u25BC</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map((addr, idx) => {
              const globalIdx = sorted.indexOf(addr);
              return (
                <tr
                  key={addr.id || idx}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#f9fafb')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  <td style={{ ...styles.td, fontWeight: 600, color: '#6b7280' }}>
                    {globalIdx + 1}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 500 }}>
                    {addr.label || '\u2014'}
                  </td>
                  <td style={styles.td}>{addr.state || '\u2014'}</td>
                  <td style={styles.td}>
                    <ScoreBar score={addr.compositeScore ?? 0} />
                  </td>
                  <td style={{ ...styles.td, ...styles.componentValue }}>
                    {(addr.components?.zScoreRank ?? 0).toFixed(2)}
                  </td>
                  <td style={{ ...styles.td, ...styles.componentValue }}>
                    {(addr.components?.volatilityRank ?? 0).toFixed(2)}
                  </td>
                  <td style={{ ...styles.td, ...styles.componentValue }}>
                    {(addr.components?.deviationRank ?? 0).toFixed(2)}
                  </td>
                  <td style={{ ...styles.td, ...styles.componentValue }}>
                    {(addr.components?.missingDataScore ?? 0).toFixed(2)}
                  </td>
                  <td style={styles.td}>
                    <AlertBadge level={addr.alertLevel || 'normal'} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!showAll && sorted.length > DEFAULT_LIMIT && (
        <div
          style={{
            textAlign: 'center',
            padding: '12px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Showing {displayed.length} of {sorted.length} addresses
        </div>
      )}
    </div>
  );
}
